import { useState, useEffect } from 'react';
import useOrganizationStore from '../store/organizationStore';
import useDealStore from '../store/dealStore';
import '../styles/organization.css';

export default function OrganizationProfilePage() {
  const { profile, saveProfile } = useOrganizationStore();
  const { navigateToModule } = useDealStore();

  const [form, setForm] = useState({ ...profile });
  const [showToast, setShowToast] = useState(false);
  const [saving, setSaving] = useState(false);

  // Keep form in sync if profile changes externally
  useEffect(() => {
    setForm({ ...profile });
  }, [profile]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      saveProfile(form);
      await new Promise(r => setTimeout(r, 500));
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        navigateToModule('entry');
      }, 1200);
    } finally {
      setSaving(false);
    }
  };

  // Auto-build signature preview
  const autoSig = [
    form.senderName,
    form.companyName,
    form.phone,
    form.contactEmail,
    form.website,
    form.officeAddress,
  ].filter(Boolean).join('\n');

  const previewSig = form.emailSignature || autoSig;

  return (
    <div className="org-profile-page">
      {/* Header */}
      <div className="org-profile-header">
        <div>
          <h2 className="org-profile-title">🏢 Organization Profile</h2>
          <p className="org-profile-subtitle">
            This information is used by the AI to personalize all emails, LOIs, lender outreach, and documents generated in AcquireOS.
          </p>
        </div>
        <button
          className={`org-profile-save-btn ${saving ? 'saving' : ''}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⟳ Saving…' : '✓ Save Changes'}
        </button>
      </div>

      {/* Section 1: Company Info */}
      <div className="org-profile-section">
        <div className="org-profile-section-title">📋 Company Information</div>
        <p className="org-profile-section-desc">
          Used in AI-generated emails, LOIs, and document headers.
        </p>
        <div className="org-form-grid">
          <div className="org-field full-width">
            <label className="org-label">Company Name <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              className="org-input"
              placeholder="Apex Capital Partners"
              value={form.companyName}
              onChange={e => handleChange('companyName', e.target.value)}
            />
          </div>
          <div className="org-field full-width">
            <label className="org-label">Website</label>
            <input
              className="org-input"
              placeholder="https://apexcapital.com"
              value={form.website}
              onChange={e => handleChange('website', e.target.value)}
            />
          </div>
          <div className="org-field full-width">
            <label className="org-label">Company Description</label>
            <textarea
              className="org-textarea"
              placeholder="Brief description of your firm and investment focus…"
              value={form.companyDescription}
              onChange={e => handleChange('companyDescription', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Section 2: Brand & Contact */}
      <div className="org-profile-section">
        <div className="org-profile-section-title">🎨 Brand & Contact Details</div>
        <p className="org-profile-section-desc">
          Contact details appear in email footers and generated documents.
        </p>
        <div className="org-form-grid">
          <div className="org-field">
            <label className="org-label">Contact Email</label>
            <input
              className="org-input"
              type="email"
              placeholder="deals@yourcompany.com"
              value={form.contactEmail}
              onChange={e => handleChange('contactEmail', e.target.value)}
            />
          </div>
          <div className="org-field">
            <label className="org-label">Phone Number</label>
            <input
              className="org-input"
              placeholder="(512) 555-0100"
              value={form.phone}
              onChange={e => handleChange('phone', e.target.value)}
            />
          </div>
          <div className="org-field full-width">
            <label className="org-label">Office Address</label>
            <input
              className="org-input"
              placeholder="123 Capital Blvd, Suite 400, Austin, TX 78701"
              value={form.officeAddress}
              onChange={e => handleChange('officeAddress', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Section 3: Email & Communication */}
      <div className="org-profile-section">
        <div className="org-profile-section-title">✉️ Email & Communication</div>
        <p className="org-profile-section-desc">
          Sender name and signature are automatically inserted into every AI-generated email.
        </p>

        <div className="org-form-grid single-col">
          <div className="org-field">
            <label className="org-label">Sender Name</label>
            <input
              className="org-input"
              placeholder="Marcus Reed"
              value={form.senderName}
              onChange={e => handleChange('senderName', e.target.value)}
            />
          </div>

          <div className="org-field">
            <label className="org-label">
              Custom Email Signature
            </label>
            <textarea
              className="org-textarea"
              style={{ minHeight: 100 }}
              placeholder={`Auto-generated:\n\n${autoSig || 'Marcus Reed\nApex Capital Partners\n...'}`}
              value={form.emailSignature}
              onChange={e => handleChange('emailSignature', e.target.value)}
            />
          </div>
        </div>

        {/* Live signature preview */}
        <div className="org-signature-preview">
          <div className="org-signature-preview-label">Live Signature Preview</div>
          <hr className="org-signature-preview-divider" />
          <div
            className="org-signature-preview-body"
            style={{ borderLeft: `3px solid ${form.brandColor}`, paddingLeft: 12 }}
          >
            {previewSig || '(your signature will appear here)'}
          </div>
        </div>
      </div>

      {/* Save button (bottom) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button
          className={`org-profile-save-btn ${saving ? 'saving' : ''}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⟳ Saving…' : '✓ Save Changes'}
        </button>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="org-save-toast">
          ✓ Organization profile saved successfully
        </div>
      )}
    </div>
  );
}
